import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { environment } from '../../../environments/environment';
import { SwUpdate } from '@angular/service-worker';
import { APIServices } from 'src/app/core/config/app-config';
import { SESSION_STORAGE, StorageService } from 'ngx-webstorage-service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, filter, catchError } from 'rxjs/operators';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { menuData, PolicyRoutes } from '../model/dataModels/menuData';
import { TypeSearch } from '../model/enums/typeSearch';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/modules/shared/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { UserCountry, UserInfo } from 'src/app/core/model/dataModels/userInfo';
import { SnackBarService } from 'src/app/modules/shared/snack-bar/snack-bar.service';
import { ModelBase } from '../model/dataModels/modelbase';

@Injectable({ providedIn: 'root' })

export class AppService 
{
    loading = false;
    userInfo: UserInfo;
    apiToken: any;
    isLogged = false;
    actualUrl = null;
    tokenOAuth: string = "";
    loginError: boolean = false;
    hideContentOnLogOut: boolean = false;
    policy: PolicyRoutes[] = [];

    private titleSubject = new BehaviorSubject<string>('HOME');
    title$ = this.titleSubject.asObservable();

    constructor(@Inject(SESSION_STORAGE) private storage: StorageService, private dialog: MatDialog,
      private http: HttpClient, private router: Router, private translate: TranslateService,
      private ngxService: NgxUiLoaderService, private swUpdate: SwUpdate,
      private snackBar: SnackBarService,) 
    {
      router.events.pipe( filter(event => event instanceof NavigationEnd) ).subscribe((event: NavigationEnd) => 
      {
        this.loading = false;
        if (!this.actualUrl || this.actualUrl.split('/')[1] !== event.url.split('/')[1]) { this.cleanGridCache(); }
        this.checkUser();
      });

      router.events.pipe( filter(event => event instanceof NavigationStart) ).
      subscribe((event: NavigationStart) => { if (event.url !== '/') { this.loading = true; } });
    }

    setTitle(key_module: string, key_action: string = '') 
    { 
      const module = " > " + this.translate.instant(key_module).toUpperCase();
      const module_action = key_action == '' ? module : module + " > " + this.translate.instant(key_action).toUpperCase();
      const title = this.translate.instant('Menu.Admin').toUpperCase() + module_action;
      this.titleSubject.next(title); 
    }

    cleanGridCache() 
    {
      if (this.storage.get('cacheGridKey')) 
      {
        this.storage.remove(this.storage.get('cacheGridKey'));
        this.storage.remove(this.storage.get('cacheGridKey') + 'QuickSearch');
      }
    }
  
    getUserMenu(roleid): Observable<any> 
    {
      const headers = {'Authorization': this.tokenOAuth};
      const endpoint = `${APIServices.roles}/${roleid}/menus`;
      return this.http.get(endpoint, {headers }).pipe(map(response => 
      { 
        let result = response as menuData[];
        this.storage.set(TypeSearch.menu, result);
        return result;
      }),
      catchError(error => throwError(() => new Error(error))));
    }

    startLoad() { this.ngxService.start(); }

    async checkLogged() 
    {
      if (this.getFromLocal(environment.userStorage) != null) 
      {
        this.tokenOAuth = localStorage.getItem('tokenOAuth')
        this.isLogged = true;
      } 
      else { this.isLogged = false; }
    }

    async hideContent(hideContent) { this.hideContentOnLogOut = hideContent; }

    stopLoad(time) 
    {
      if(time !== null) { setTimeout(() => { this.ngxService.stop(); }, time); }
      else { this.ngxService.stop(); }
    }

    checkUser() 
    {
      if (this.getFromLocal(environment.userStorage) == null) { this.router.navigate(['/login']); } 
      else { this.userInfo = this.getFromLocal(environment.userStorage); }
      if (this.userInfo != null && this.swUpdate.isEnabled) 
      {
        this.swUpdate.checkForUpdate().then(() => this.swUpdate.activateUpdate().then(() => document.location.reload()));
        this.saveInLocal(environment.userStorage, this.userInfo);
      }
    }

    getInternationalizationType(langualeCode) 
    {
      if (!langualeCode)  { return 'en-EN'; }
      switch (langualeCode) 
      {
        case 1:
          return 'es-ES';
        case 2:
          return 'en-EN';
        case 3:
          return 'pt';
        default:
          return 'en-EN';
      }
    }
  
    saveInLocal(key, val) 
    {
      this.storage.set(key, val);
      if (key === environment.userStorage) { this.userInfo = val; }
      if (key === environment.jwt) { this.apiToken = val; }
    }
  
    getFromLocal(key) 
    {
      if (key === environment.userStorage) { this.userInfo = this.storage.get(key); }
      if (key === environment.jwt) { this.apiToken = this.storage.get(key); }
      return this.storage.get(key);
    }
  
    getRolesAndPermisions() { const user = this.getFromLocal(environment.userStorage); return user.rolesid; }
    getPolicies() { return this.getFromLocal("policy") as PolicyRoutes[]; }

    public ReturnBodySetToken(result: HttpResponse<Object>) : Object
    {
      var jwt = result.headers.get("token");
      if (jwt != null) { localStorage.setItem('token', jwt); }    
      return result.body;
    }

    public OpenDialogConfirmation(onAccept: () => void, key_title: string = "Common.DeleteConfirmation") : void
    {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, 
      {
        data: { title: key_title }, disableClose: true, autoFocus: true, width: "50%",
      });
      dialogRef.afterClosed().subscribe(result => { if(result == true) { onAccept(); } });
    }

    public ValidateError(error: any) : void
    {
      let key_message = 'Messages.SaveFail';
      const response_key = error?.error;
      if(response_key) { key_message = ('Messages.' + response_key); }
      this.snackBar.error(key_message);
    }

    public GetCountries(): Observable<UserCountry[]> 
    {
      const headers = {'Authorization': this.tokenOAuth};
      return this.http.get<UserCountry[]>(APIServices.usercountries, {headers}).pipe(map(result => { return result; }));
    }

    public GetLanguages(): Observable<any[]> 
    {
      const headers = {'Authorization': this.tokenOAuth};
      return this.http.get<any[]>(APIServices.languages, {headers}).pipe(map(result => { return result; }));
    }

    public GetAllStatus(): Observable<ModelBase[]> 
    {
      const headers = {'Authorization': this.tokenOAuth};
      return this.http.get<ModelBase[]>(APIServices.status, {headers}).pipe(map(result => { return result; }));
    }

    public GetAvailableStatus(statusid: number): Observable<ModelBase[]> 
    {
      const headers = {'Authorization': this.tokenOAuth};
      return this.http.get<ModelBase[]>(`${APIServices.status}/${statusid}/availables`, {headers}).pipe(map(result => { return result; }));
    }
    
    public TranslateCountry(Country: UserCountry): string
    {
      const key = `Countries.${Country.isocode}`;
      const translated = this.translate.instant(key);
      return translated === key ? Country.description : translated;
    }

    public formatDateOnly(value: Date | string): string
    {
      const date = value instanceof Date ? value : new Date(value);

      if (isNaN(date.getTime())) { return ''; }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    }

    public Download(blob: Blob, fileName: string)
    {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }

}